/**
 * WordSkeleton: renders the audience-visible word placeholder.
 *
 * M4 batch 6b. Per PRD §3.5, audience clients see the drawer's word as a
 * skeleton: spaces + punctuation are visible, alpha-numeric letters appear
 * as underscores unless their index is in the `revealedIndices` set (the
 * server-driven hint scheduler: see `cumulativeHintsThrough`).
 *
 *   "ice cream", revealedIndices=[0, 4]  →  "i _ _   c _ _ _ _"
 *   "don't",     revealedIndices=[]      →  "_ _ _ ' _"
 *
 * The component is intentionally length-aware: callers pass `wordLength`
 * (the canonical length the server preserved across redaction) along with
 * the (possibly empty) `word` string. When `word === ""` the skeleton
 * renders pure underscores spaced by `wordLength`; this is the spectator
 * path where even revealed letters are stripped.
 *
 * Layout: each character is a `<span>` so future styling (e.g. animating
 * a hint reveal) can grab it individually.
 */

type WordSkeletonProps = {
  /**
   * Drawer-canonical word OR `""` for fully-redacted (audience-pending +
   * spectator). When `""`, the component falls back to `wordLength`
   * underscores; revealed positions are filled from `revealedLetters`
   * (M4 batch 6c: the audience-pending recipient's only path to actual
   * letters since the word is blanked).
   */
  word: string;
  /** Canonical length of the word in code units (preserved across redaction). */
  wordLength: number;
  /**
   * v4 T4, redaction-safe structural skeleton ("___ _____"): letters/digits
   * are "_", spaces + punctuation are verbatim. The server ships this for the
   * `word === ""` recipients (audience-pending / spectator) so the placeholder
   * shows word breaks. When non-empty it drives the blank-word render (its
   * length is authoritative). Defaults to `""` for callsites / pre-T4 frames
   * that don't plumb it, which falls back to the `wordLength` underscore row.
   * Accepts `undefined` explicitly (the view-model field is optional), the
   * `= ""` default normalizes it.
   */
  wordPattern?: string | undefined;
  /** Letter positions to reveal (audience-pending only). Spectators pass []. */
  revealedIndices: ReadonlyArray<number>;
  /**
   * M4 batch 6c: index + character pairs for revealed positions. When
   * `word === ""` (redacted), this is the *only* source of the actual
   * characters to render at revealed positions; otherwise it duplicates
   * `word.charAt(index)` and the component prefers reading from `word`
   * directly. Spectators pass `[]` (no hints).
   *
   * Defaults to `[]` for callsites that don't yet plumb it through.
   */
  revealedLetters?: ReadonlyArray<{ index: number; char: string }>;
  /**
   * When true, render the full word verbatim (drawer + audience-correct).
   * Overrides the skeleton entirely.
   */
  showFullWord?: boolean;
};

/**
 * Per PRD §3.5: spaces and punctuation are always visible in the
 * skeleton; only alphanumeric letters get hidden. We treat anything that
 * isn't a letter or digit as "always visible", matches the canonical
 * `cumulativeHintsThrough` rule of "only reveal alpha letters".
 */
function isHiddenInSkeleton(ch: string): boolean {
  // Letters and digits get hidden; everything else (spaces, punctuation,
  // emoji) shows through.
  return /\p{L}|\p{N}/u.test(ch);
}

export function WordSkeleton({
  word,
  wordLength,
  wordPattern = "",
  revealedIndices,
  revealedLetters = [],
  showFullWord = false,
}: WordSkeletonProps) {
  if (showFullWord && word.length > 0) {
    return (
      <span class="word-skeleton word-skeleton--full" data-testid="word-skeleton">
        {word}
      </span>
    );
  }

  // No drawer word visible: render from `wordLength`. Used for
  // spectators (no reveals) and for audience-pending when the server
  // hasn't sent us a word string but has sent a length.
  //
  // M4 batch 6c: when `revealedLetters` are present we can still render
  // the actual character at each revealed position (e.g. "_ _ _ c _ e _
  // _ _": see PRD §3.5). Spectators pass `revealedLetters = []` so
  // they fall through to pure underscores.
  if (word.length === 0) {
    const letterByIndex = new Map<number, string>();
    for (const { index, char } of revealedLetters) letterByIndex.set(index, char);

    // v4 T4: when the server ships a structural `wordPattern` ("___ _____"),
    // render one slot per pattern character so multi-word skeletons break at
    // the real spaces. The pattern carries no letters (redaction-safe), so it
    // is safe for audience-pending + spectator. Revealed hint letters overlay
    // by index (audience-pending only; spectators pass `revealedLetters=[]`).
    // No artificial inter-slot spacing, the literal pattern spaces are the
    // word breaks and CSS (`.word-skeleton { letter-spacing }`) separates the
    // underscore slots, matching the mixed-mode (real-word) render exactly.
    if (wordPattern.length > 0) {
      return (
        <span class="word-skeleton word-skeleton--blank" data-testid="word-skeleton">
          {Array.from({ length: wordPattern.length }, (_unused, i) => {
            const slot = wordPattern[i]!;
            const revealed = letterByIndex.get(i);
            if (revealed !== undefined) {
              return (
                <span class="word-skeleton-slot word-skeleton-slot--revealed" key={i}>
                  <span
                    class="word-skeleton-char word-skeleton-char--revealed"
                    data-testid={`word-skeleton-revealed-${i}`}
                  >
                    {revealed}
                  </span>
                </span>
              );
            }
            // "_" = hidden letter; anything else is literal structure
            // (space / punctuation) and shows through.
            const isHiddenSlot = slot === "_";
            return (
              <span
                class={
                  isHiddenSlot
                    ? "word-skeleton-slot"
                    : "word-skeleton-slot word-skeleton-slot--literal"
                }
                key={i}
              >
                {slot}
              </span>
            );
          })}
        </span>
      );
    }

    // Pre-T4 fallback: no pattern available → length-only underscore row.
    return (
      <span class="word-skeleton word-skeleton--blank" data-testid="word-skeleton">
        {Array.from({ length: wordLength }, (_, i) => {
          const ch = letterByIndex.get(i);
          if (ch !== undefined) {
            return (
              <span class="word-skeleton-slot word-skeleton-slot--revealed" key={i}>
                <span
                  class="word-skeleton-char word-skeleton-char--revealed"
                  data-testid={`word-skeleton-revealed-${i}`}
                >
                  {ch}
                </span>
                {i < wordLength - 1 ? " " : ""}
              </span>
            );
          }
          return (
            <span class="word-skeleton-slot" key={i}>
              _{i < wordLength - 1 ? " " : ""}
            </span>
          );
        })}
      </span>
    );
  }

  // Mixed mode: render letter-by-letter; hidden letters become "_"
  // unless their index is in revealedIndices.
  const revealedSet = new Set(revealedIndices);
  const cells: Array<{ ch: string; revealed: boolean; hidden: boolean }> = [];
  for (let i = 0; i < word.length; i++) {
    const ch = word[i]!;
    const hidden = isHiddenInSkeleton(ch);
    cells.push({
      ch,
      revealed: revealedSet.has(i),
      hidden,
    });
  }

  return (
    <span class="word-skeleton" data-testid="word-skeleton">
      {cells.map((cell, i) => {
        // Spaces / punctuation always show through.
        if (!cell.hidden) {
          return (
            <span class="word-skeleton-char word-skeleton-char--literal" key={i}>
              {cell.ch}
            </span>
          );
        }
        // Revealed letters show the literal character.
        if (cell.revealed) {
          return (
            <span
              class="word-skeleton-char word-skeleton-char--revealed"
              key={i}
              data-testid={`word-skeleton-revealed-${i}`}
            >
              {cell.ch}
            </span>
          );
        }
        // Hidden letter → underscore.
        return (
          <span class="word-skeleton-char word-skeleton-char--hidden" key={i}>
            _
          </span>
        );
      })}
    </span>
  );
}
